import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, type WalletAdjustment } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, StatTile, type Column, type BadgeTone } from '@/components/ui';
import { AdminPage, AdminPageHeader } from '@/components/admin/shared';
import { dateTime } from '@/lib/adminFormat';
import { getAdminProfile } from '@/lib/adminAuth';

type StatusFilter = 'pending' | 'applied' | 'rejected' | 'all';

/** WalletApprovals — the CHECKER side of maker-checker dual-control. Manual wallet
 *  credits/debits queued by a maker (on the Wallet page) land here as PENDING; a
 *  DIFFERENT super-admin approves (money moves) or rejects (nothing moves). The
 *  two-person rule is enforced by admin-svc AND the database; the UI just disables
 *  the buttons on adjustments you created so you never even attempt a self-approve. */
export function WalletApprovals() {
  const qc = useQueryClient();
  const me = getAdminProfile();
  const [filter, setFilter] = useState<StatusFilter>('pending');

  const queryKey = ['admin', 'wallet-adjustments', filter];
  const { data, isLoading, isError, error } = useQuery({
    queryKey,
    queryFn: ({ signal }) => adminApi.listWalletAdjustments(filter === 'all' ? undefined : filter, signal),
  });
  const rows = data?.adjustments ?? [];

  const approve = useMutation({
    mutationFn: (id: number) => adminApi.approveWalletAdjustment(id),
    onSettled: () => qc.invalidateQueries({ queryKey: ['admin', 'wallet-adjustments'] }),
  });
  const reject = useMutation({
    mutationFn: (id: number) => adminApi.rejectWalletAdjustment(id),
    onSettled: () => qc.invalidateQueries({ queryKey: ['admin', 'wallet-adjustments'] }),
  });
  const busy = approve.isPending || reject.isPending;
  const actionError = (approve.error as Error)?.message || (reject.error as Error)?.message || null;

  const pendingCount = filter === 'pending' ? rows.length : undefined;

  const columns: Array<Column<WalletAdjustment>> = [
    { key: 'id', header: '#', cell: (r) => <span className="metric text-xs text-muted">{r.id}</span> },
    { key: 'when', header: 'Requested', cell: (r) => <span className="text-xs text-muted">{dateTime(r.created_at)}</span> },
    {
      key: 'target',
      header: 'Wallet',
      cell: (r) => (
        <span className="text-xs">
          <Badge tone={r.scope === 'driver' ? 'warn' : 'info'}>{r.scope}</Badge>{' '}
          <span className="text-slate-300">#{r.target_user_id}</span>
        </span>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      cell: (r) => (
        <span className={`metric text-sm ${r.direction === 'debit' ? 'text-danger' : 'text-ok'}`}>
          {r.direction === 'debit' ? '−' : '+'}${r.dollars.toFixed(2)}
        </span>
      ),
    },
    { key: 'note', header: 'Note', cell: (r) => <span className="text-xs text-slate-300 truncate" title={r.note}>{r.note || '—'}</span> },
    { key: 'maker', header: 'Requested by', cell: (r) => <span className="text-xs text-muted" title={`admin #${r.maker_admin_id}`}>{r.maker_email || `#${r.maker_admin_id}`}</span> },
    { key: 'status', header: 'Status', cell: (r) => <AdjStatusBadge r={r} /> },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (r) => {
        if (r.status !== 'pending') return <span className="text-xs text-muted">—</span>;
        const isOwn = me?.id === r.maker_admin_id;
        if (isOwn) {
          return <span className="text-[11px] text-muted" title="You created this — a different super-admin must approve it.">your request</span>;
        }
        return (
          <div className="flex justify-end gap-1.5">
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                if (window.confirm(`Approve & APPLY this ${r.direction} of $${r.dollars.toFixed(2)} to ${r.scope} #${r.target_user_id}? Money will move.`)) {
                  approve.mutate(r.id);
                }
              }}
              className="btn-primary px-2 py-1 text-xs"
            >
              Approve
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                if (window.confirm('Reject this adjustment? No money moves.')) reject.mutate(r.id);
              }}
              className="btn-secondary px-2 py-1 text-xs text-danger"
            >
              Reject
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <AdminPage>
      <AdminPageHeader
        title="Wallet Approvals"
        subtitle="Maker-checker dual-control. Manual wallet credits/debits await a SECOND super-admin's approval before any money moves — you cannot approve an adjustment you created."
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatTile label="Pending in queue" value={pendingCount ?? '—'} tone={pendingCount ? 'warn' : 'neutral'} hint="awaiting a checker" />
      </div>

      <div className="flex items-center gap-2">
        <span className="text-[11px] uppercase tracking-wide text-muted">Show</span>
        {(['pending', 'applied', 'rejected', 'all'] as StatusFilter[]).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setFilter(s)}
            className={`rounded px-2 py-1 text-xs capitalize ${filter === s ? 'bg-ink-700 text-slate-100' : 'text-muted hover:text-slate-300'}`}
          >
            {s}
          </button>
        ))}
      </div>

      {actionError ? <p className="text-xs text-danger">{actionError}</p> : null}

      <Card flush>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner label="Loading queue…" />
          </div>
        ) : isError ? (
          <EmptyState title="Couldn't load adjustments" description={(error as Error)?.message} />
        ) : (
          <DataTable<WalletAdjustment>
            columns={columns}
            rows={rows}
            rowKey={(r) => String(r.id)}
            stickyHeader
            empty={<EmptyState title={filter === 'pending' ? 'Nothing awaiting approval' : 'No adjustments'} description={filter === 'pending' ? 'New manual adjustments queued on the Wallet page will appear here.' : 'No adjustments match this filter.'} />}
          />
        )}
      </Card>
    </AdminPage>
  );
}

function AdjStatusBadge({ r }: { r: WalletAdjustment }) {
  const tone: BadgeTone = r.status === 'applied' ? 'ok' : r.status === 'rejected' ? 'danger' : 'warn';
  const label = r.status === 'applied' && r.checker_email ? `applied · ${r.checker_email}` : r.status;
  return <Badge tone={tone}>{label}</Badge>;
}
